import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useNewEmployeeForm } from '@/hooks/useNewEmployeeForm';
import { EmployeeDocumentsSection } from '@/components/company/EmployeeDocumentsSection';
import { CountryCombobox } from '@/components/insurance/CountryCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function NewEmployeePage() {
  const navigate = useNavigate();
  const { currentCompany } = useCompanyContext();
  const form = useNewEmployeeForm();

  if (!currentCompany) return null;

  return (
    <div className="min-h-full bg-background-secondary p-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/company/employees')}>
          <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
          Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">Add Employee</h1>
      </div>

      <form
        onSubmit={e => form.handleSubmit(e, currentCompany._id)}
        className="space-y-6 max-w-2xl"
      >
        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="w-4 h-4 text-primary" aria-hidden="true" />
              Basic Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">
                Name{' '}
                <span className="text-destructive" aria-hidden="true">
                  *
                </span>
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={e => form.setName(e.target.value)}
                placeholder="Full name"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={e => form.setEmail(e.target.value)}
                  placeholder="employee@company.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phoneNumber}
                  onChange={e => form.setPhoneNumber(e.target.value)}
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                value={form.position}
                onChange={e => form.setPosition(e.target.value)}
                placeholder="e.g. Sales Manager"
              />
            </div>
          </CardContent>
        </Card>

        {/* Personal Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Personal Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={form.postalCode}
                  onChange={e => form.setPostalCode(e.target.value)}
                  placeholder="12345"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={e => form.setCity(e.target.value)}
                  placeholder="e.g. Amsterdam"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="identityDocumentNumber">ID / Passport Number</Label>
              <Input
                id="identityDocumentNumber"
                value={form.identityDocumentNumber}
                onChange={e => form.setIdentityDocumentNumber(e.target.value)}
                placeholder="AB123456"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nationality</Label>
                <CountryCombobox
                  value={form.nationality}
                  onChange={form.setNationality}
                  placeholder="Select nationality"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={e => form.setDateOfBirth(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <EmployeeDocumentsSection
          identityDocFront={form.identityDocFront}
          identityDocBack={form.identityDocBack}
          drivingLicenseFront={form.drivingLicenseFront}
          drivingLicenseBack={form.drivingLicenseBack}
          editable
          onDocumentChange={form.handleDocumentChange}
        />

        {/* Submit */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/company/employees')}
            disabled={form.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={form.isSubmitting || !form.name.trim()}
            aria-busy={form.isSubmitting}
          >
            {form.isSubmitting ? 'Adding...' : 'Add Employee'}
          </Button>
        </div>
      </form>
    </div>
  );
}
